import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronRight, DoorOpen, Lock, Map } from "lucide-react";
import { api } from "../lib/api";
import { IMAGES } from "../lib/tokens";
import { STOPS, worldCount } from "../lib/monorail";
import { useWorld } from "../state/World";

/**
 * The Monorail.
 *
 * The Tour is the first prompt. A World has the same cold-start problem as a
 * chat box — without an opening move a human does not know what to do with it —
 * so MelzAI hosts a linear ride past every Place before the World opens.
 *
 * Exteriors only, by design. Nothing inside a Space is ever pre-filled, which
 * is exactly why this can never break: it carries no state at all.
 */
export default function Monorail() {
  const { me, refresh } = useWorld();
  const nav = useNavigate();
  const [i, setI] = useState(-1); // -1 = MelzAI introduces himself
  const [drive, setDrive] = useState(true);
  const { places, open } = worldCount();

  const stop = STOPS[i];
  const last = i >= STOPS.length - 1;

  const release = useCallback(async () => {
    try {
      await api.post("/world/toured", {});
      await refresh();
    } catch (err) {
      // The World opens regardless — never trap anyone in the tour.
      // Logged for observability; behaviour is unchanged.
      console.debug("[Monorail] /world/toured failed, continuing:", err);
    }
    nav("/");
  }, [nav, refresh]);

  const next = useCallback(() => {
    if (last) return release();
    setI((v) => v + 1);
  }, [last, release]);

  // It drives, like a monorail. You can grab the wheel at any time.
  useEffect(() => {
    if (!drive || last) return;
    const t = setTimeout(next, i < 0 ? 6200 : 7000);
    return () => clearTimeout(t);
  }, [drive, i, last, next]);

  useEffect(() => {
    const key = (e) => {
      if (["Enter", " ", "ArrowRight"].includes(e.key)) {
        e.preventDefault();
        setDrive(false);
        next();
      }
      if (e.key === "ArrowLeft") {
        setDrive(false);
        setI((v) => Math.max(-1, v - 1));
      }
      if (e.key === "Escape") release();
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [next, release]);

  return (
    <div
      className="relative h-screen cursor-pointer overflow-hidden bg-[#05070c]"
      data-testid="monorail"
      data-stop={stop?.key || "intro"}
      onClick={() => {
        setDrive(false);
        next();
      }}
    >
      <AnimatePresence mode="wait">
        <motion.img
          key={stop?.key || "intro"}
          src={IMAGES[stop?.plate || "atlasHero"] || IMAGES[STOPS[0].plate]}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ opacity: 0, scale: 1.09 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.03 }}
          transition={{ duration: 1.5, ease: [0.2, 0.8, 0.2, 1] }}
        />
      </AnimatePresence>

      {/* Edge vignettes, not a scrim: the art stays bright through the middle
          half of the frame and only the top and bottom margins darken, the way
          a film title card does. Without this the copy is unreadable on the
          brighter plates. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-[46%]"
        style={{
          background:
            "linear-gradient(to top, rgba(3,5,9,0.96) 0%, rgba(3,5,9,0.84) 24%, rgba(3,5,9,0.44) 56%, rgba(3,5,9,0) 100%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-20 h-[16%]"
        style={{
          background:
            "linear-gradient(to bottom, rgba(3,5,9,0.72) 0%, rgba(3,5,9,0.3) 55%, rgba(3,5,9,0) 100%)",
        }}
      />

      {/* the ride: one lit pip per stop */}
      <div className="absolute left-8 top-7 z-30 flex items-center gap-4 md:left-14 md:top-10">
        <span className="tracked text-[9px] text-white/70">
          {places} Places · <span className="text-[var(--stage-gold)]">{open} open today</span>
        </span>
        <span className="flex gap-1.5">
          {STOPS.map((s, n) => (
            <span
              key={s.key}
              className="h-[2px] w-5 rounded-full transition-colors duration-700"
              style={{
                background: n <= i ? "var(--stage-gold)" : "rgba(255,255,255,0.2)",
              }}
            />
          ))}
        </span>
      </div>

      {/* MelzAI narrates from the bottom left, on the quiet part of the frame */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 p-8 md:p-14">
        <AnimatePresence mode="wait">
          <motion.div
            key={stop?.key || "intro"}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="max-w-xl"
          >
            {stop ? (
              <>
                <div className="mb-2.5 flex flex-wrap items-center gap-3">
                  <span className="tracked text-[9px] text-[var(--stage-gold)]">
                    {stop.name}
                  </span>
                  <span className="mono text-[8px] uppercase text-white/45">
                    {stop.clock}
                  </span>
                  <span
                    className="mono flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[8px]"
                    style={{
                      borderColor: stop.open
                        ? "rgba(224,164,74,0.5)"
                        : "rgba(255,255,255,0.16)",
                      color: stop.open ? "var(--stage-gold)" : "rgba(255,255,255,0.4)",
                    }}
                    data-testid={stop.open ? "stop-open" : "stop-closed"}
                  >
                    {stop.open ? <DoorOpen size={9} /> : <Lock size={9} />}
                    {stop.open ? `Open · ${stop.practice}` : "Not Open Yet"}
                  </span>
                </div>
                <p
                  data-testid="monorail-line"
                  className="serif text-2xl font-light italic leading-snug text-[#f4ece0] sm:text-3xl"
                  style={{ textShadow: "0 2px 26px rgba(0,0,0,0.9)" }}
                >
                  {stop.tagline}
                </p>
                <p
                  data-testid="monorail-body"
                  className="mt-3 max-w-md text-[14px] leading-relaxed text-white/70"
                  style={{ textShadow: "0 2px 18px rgba(0,0,0,0.95)" }}
                >
                  {stop.body}
                </p>
              </>
            ) : (
              <>
                <div className="tracked mb-2.5 text-[9px] text-[var(--stage-gold)]">
                  MelzAI
                </div>
                <p
                  data-testid="monorail-line"
                  className="serif text-2xl font-light leading-snug text-[#f4ece0] sm:text-3xl"
                  style={{ textShadow: "0 2px 26px rgba(0,0,0,0.9)" }}
                >
                  I am MelzAI. I will not organise you and I will not sell you
                  anything — I hold your side of the bargain. Before you go anywhere,
                  let me show you the World. {places} Places. {open} of them open
                  today.
                </p>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="absolute bottom-8 right-8 z-30 flex items-center gap-4 md:bottom-14 md:right-14">
        <button
          type="button"
          data-testid="monorail-skip"
          onClick={(e) => {
            e.stopPropagation();
            release();
          }}
          className="tracked text-[9px] text-white/55 transition-colors hover:text-white"
        >
          Skip the tour
        </button>
        <button
          type="button"
          data-testid="monorail-next"
          onClick={(e) => {
            e.stopPropagation();
            setDrive(false);
            next();
          }}
          className="tracked group flex items-center gap-2 rounded-full border border-[var(--stage-gold)]/45 px-5 py-2.5 text-[9px] text-[var(--stage-gold)]/85 transition-colors hover:border-[var(--stage-gold)] hover:text-[var(--stage-gold)]"
        >
          {last ? "Open the World" : i < 0 ? "Begin" : "Next stop"}
          {last ? (
            <Map size={12} strokeWidth={2} />
          ) : (
            <ChevronRight
              size={12}
              strokeWidth={2}
              className="transition-transform group-hover:translate-x-0.5"
            />
          )}
        </button>
      </div>

      {drive && !last && (
        <button
          type="button"
          data-testid="monorail-take-over"
          onClick={(e) => {
            e.stopPropagation();
            setDrive(false);
          }}
          className="tracked absolute bottom-8 left-1/2 z-30 -translate-x-1/2 text-[9px] text-white/40 transition-colors hover:text-white/80 md:bottom-14"
        >
          Let me take it from here
        </button>
      )}

      {/* Stepping straight in from the tour, if a door is actually open. */}
      {stop?.open && (
        <motion.button
          type="button"
          data-testid="monorail-enter"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
          onClick={async (e) => {
            e.stopPropagation();
            await api.post("/world/toured", {}).catch(() => {});
            await refresh();
            nav(stop.to);
          }}
          className="tracked absolute right-8 top-8 z-30 flex items-center gap-2 rounded-full bg-[var(--stage-gold)] px-5 py-2.5 text-[9px] uppercase text-[#1a1206] transition-all hover:brightness-110 md:right-14 md:top-12"
        >
          Step into {stop.name} <ArrowRight size={12} strokeWidth={2} />
        </motion.button>
      )}

      {!me && (
        <span className="mono absolute left-8 bottom-8 z-30 text-[9px] text-white/25 md:left-14">
          nothing to sign up for
        </span>
      )}
    </div>
  );
}
